import { useEffect } from 'react';
import { compareWithAppVersion } from '@croquiscom/web2app';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';
import { Typography } from '@common/styles';

import { PlayIcon } from '../assets/PlayIcon';
import { useVideoService } from '../services/useVideoService';
import { ShortClipModel } from '../utils/types';

export interface ShortClipCardProps {
  isReadyToLoadVideo: boolean;
  isActive: boolean;
  data: ShortClipModel;
  onImpression?: () => void;
  onClick?: () => void;
}

export function ShortClipCard({ data, isReadyToLoadVideo, isActive, onImpression, onClick }: ShortClipCardProps) {
  const { targetRef, isRevealed } = useOnceReveal();
  const { videoRef, isPlayable, playVideo } = useVideoService({
    data,
    isActive,
  });

  const handleClick = () => {
    if (compareWithAppVersion('7.47.0')) {
      onClick?.();
      return;
    }

    if (isPlayable) {
      playVideo();
    }
  };

  useEffect(() => {
    if (isRevealed) {
      onImpression?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed]);

  return (
    <Wrapper ref={targetRef} onClick={handleClick}>
      {isReadyToLoadVideo && (
        <div data-vjs-player='true'>
          <video
            style={{ backgroundImage: `url('${data.thumbnail_image_url}')`, backgroundSize: 'cover' }}
            ref={videoRef}
            poster={data.thumbnail_image_url}
            className='video-js'
            playsInline
          />
        </div>
      )}
      {isPlayable && (
        <Control>
          <PlayIcon />
        </Control>
      )}
      <Dimmed />
      <HalfDimmed />
      <Title className={Typography.BODY_15_REGULAR}>{data.title}</Title>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vw * 1.17);
  max-height: 550px;
  overflow: hidden;
  border-radius: 8px;

  video {
    object-fit: cover;
    border-radius: 8px;
    background-color: ${vars.v3Color.gray30};
  }

  .video-js {
    position: fixed;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    background-color: ${vars.v3Color.gray30};

    @media (min-width: 740px) {
      position: relative;
      width: 100%;
    }

    .vjs-tech {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .vjs-modal-dialog,
  .vjs-big-play-button {
    display: none;
  }

  .vjs-poster {
    border-radius: 8px;
    background-size: cover;
    background-color: ${vars.v3Color.gray30};

    img {
      object-fit: cover;
    }
  }
`;

const Control = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0 0 0 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.5);
  /* backdrop-filter: blur(5px); */
  border-radius: 60px;
  z-index: 99;
`;

const Dimmed = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${vars.v3Color.staticBlack};
  border-radius: 8px;
  opacity: 0.06;
`;

const HalfDimmed = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%);
  border-radius: 8px;
`;

const Title = styled.span`
  position: absolute;
  bottom: 28px;
  padding: 0 24px;
  line-height: 140%;
  color: ${vars.v3Color.staticWhite};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
